import React, { useEffect } from "react";
import {
  BarChart,
  Bar,
  Rectangle,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import Select from "react-select";
import { useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { formatDate } from "../helpers/functions";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import { selectUser } from "../store/userSlice";

// const data = Array(20)
//   .fill([
//     {
//       name: "Page C",
//       items: 2000,
//       records: 9800,
//       amt: 2290,
//     },
//     {
//       name: "Page D",
//       items: 2780,
//       records: 3908,
//       amt: 2000,
//     },
//     {
//       name: "Page E",
//       items: 1890,
//       records: 4800,
//       amt: 2181,
//     },
//     {
//       name: "Page F",
//       items: 2390,
//       records: 3800,
//       amt: 2500,
//     },
//     {
//       name: "Page G",
//       items: 3490,
//       records: 4300,
//       amt: 2100,
//     },
//   ])
//   .flat();

const groupByOptions = [
  { value: "1 minute", label: "Group by: 1 minute" },
  { value: "5 minute", label: "Group by: 5 minute" },
  { value: "15 minute", label: "Group by: 15 minute" },
  { value: "30 minute", label: "Group by: 30 minute" },
  { value: "1 hour", label: "Group by:1 hour" },
  { value: "3 hour", label: "Group by: 3 hour " },
  { value: "6 hour", label: "Group by: 6 hour" },
  { value: "12 hour", label: "Group by: 12 hour" },
  { value: "1 day", label: "Group by: 1 day" },
  { value: "2 day", label: "Group by: 2 day" },
  { value: "3 day", label: "Group by: 3 day" },
  { value: "7 day", label: "Group by: 7 day" },
  { value: "14 day", label: "Group by: 14 day" },
  { value: "1 month", label: "Group by: 1 month" },
  { value: "3 month", label: "Group by: 3 month" },
  { value: "6 month", label: "Group by: 6 month" },
  { value: "12 month", label: "Group by: 12 month" },
];
export default function MyGraph({ page, title }) {
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  let [searchParams, setSearchParams] = useSearchParams();

  // pagination in search params
  const limit = parseInt(searchParams.get("limit") || 10);
  const offset = parseInt(searchParams.get("offset") || 0);
  // order
  const order = searchParams.get("order") || "desc";
  const orderBy = searchParams.get("orderBy") || "date_at";
  // filters in search params
  const groupByValue = searchParams.get("group_by") || "1 hour";
  const dbRef = searchParams.get("dbRef") || "";
  const countries = searchParams.getAll("countries");
  const accounts = searchParams.getAll("accounts");
  const comms = searchParams.getAll("comms");
  const customers = searchParams.getAll("customers");
  const parents = searchParams.getAll("parents");
  const status = searchParams.getAll("status");
  const startDate = new Date(
    searchParams.get("startDate") ||
      new Date(new Date().setDate(new Date().getDate() - 1))
  );
  const endDate = new Date(searchParams.get("endDate") || new Date());

  const getRecords = async () => {
    let headersList = {
      Accept: "*/*",
    };
    setLoading(true);
    let response = await fetch(
      `/auth/${page}/graph?db_ref=${dbRef}&group_by=${groupByValue}${accounts
        .map((a) => `&account_id=${a}`)
        .join("")}${parents.map((a) => `&parent_id=${a}`).join("")}${customers
        .map((a) => `&customer_id=${a}`)
        .join("")}${comms.map((c) => `&kind=${c}`).join("")}${countries
        .map((c) => `&mcc=${c}`)
        .join("")}&start_date=${formatDate(startDate)}&end_date=${formatDate(
        endDate
      )}${status
        .map((s) => `&status=${s}`)
        .join(
          ""
        )}&order=${order}&order_by=${orderBy}&limit=${limit}&offset=${offset}`,
      {
        method: "GET",
        headers: headersList,
      }
    );

    let data = await response.json();
    setTimeout(() => {
      setLoading(false);
    }, 500);
    if (response.ok) {
      setData(data.data);
      setTotal(data.total);
    } else {
      toast.error(data.error);
      if (response.status === 401) {
        if (user) {
          toast.error("Your session has expired. You need to login again.");
        }
        navigate("/login", { state: { from: location } });
      }
    }
  };

  useEffect(() => {
    if (dbRef) {
      getRecords();
    }
  }, [location, groupByValue]);

  return (
    <>
      <div className="row my-3">
        <div className="col-3">
          <h3>{title}</h3>
        </div>
        <div className="col-12 col-md-3 offset-md-6">
          <Select
            value={groupByOptions.find((o) => o.value === groupByValue)}
            onChange={(vl) => {
              searchParams.set("group_by", vl.value);
              setSearchParams(searchParams);
            }}
            options={groupByOptions}
            maxMenuHeight={150}
          />
        </div>
      </div>
      <ResponsiveContainer height={300} className={"position-relative"}>
        {loading && (
          <div className="position-absolute w-100 h-100 opacity-50 ">
            <div
              className="position-absolute start-50 translate-middle fs-1"
              style={{ top: 120 }}
            >
              <FontAwesomeIcon icon={faSpinner} spin /> Loading
            </div>
          </div>
        )}

        <BarChart
          data={data}
          margin={{
            top: 5,
            right: 0,
            left: 0,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="Intervals" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar
            dataKey="Items"
            fill="gray"
            activeBar={<Rectangle fill="gray" stroke="black" />}
          />
          {page === "records" && (
            <Bar
              dataKey="Records"
              fill="darkgray"
              activeBar={<Rectangle fill="darkgray" stroke="black" />}
            />
          )}
        </BarChart>
      </ResponsiveContainer>
    </>
  );
}
